import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import CardWrapper from "../../../components/cardWrapper";
import useStepper from "../../../components/stepper/hooks/useStepper";
import { formatToCurrency } from "../../../utils/numbers";
import { DESIGN_CATEGORY, LAYOUT_TYPES } from "../../../constants/designs";
import { DesignState } from "../../../features/designSlice";
import { CURRENCY } from "../../../constants/common";
import LayoutSize from "../../../models/furniture/LayoutSize";
import LayoutBody from "../../../models/furniture/LayoutBody";
import Shape from "../../../models/furniture/Shape";
import Material from "../../../models/furniture/Material";


function OptionsContainer({
  options,
  isLoadingOptions = false,
  selectLayout,
  selectShape,
  selectMaterial,
}: {
  options: LayoutSize[] | LayoutBody[] | Shape[] | Material[];
  isLoadingOptions?: boolean;
  selectLayout: (layout: LayoutBody | LayoutSize) => void;
  selectShape: (selectedShape: Shape, elementOrder: number) => void;
  selectMaterial: (
    selectedMaterial: Material,
    elementOrder: number,
    materialOrder: number
  ) => void;
}) {
  const [showAllMaterialTypeOptions, setShowAllMaterialTypeOptions] = useState<{
    [key: string]: boolean;
  }>({});
  const { currentStepData } = useStepper();

  const isLayout = currentStepData?.category === DESIGN_CATEGORY.LAYOUT;
  const isMaterial = currentStepData?.category === DESIGN_CATEGORY.MATERIAL;

  const groupedMaterialOptions = useMemo(() => {
    const grouped: { [key: string]: Material[] } = {};
    if (!isMaterial) {
      return grouped;
    }

    options.forEach((option) => {
      const { material_subtype } = option as Material;
      if (!grouped[material_subtype]) {
        grouped[material_subtype] = [];
      }
      grouped[material_subtype].push(option as Material);
    });

    return grouped;
  }, [options]);

  if (isLoadingOptions) {
    return (
      <div className="flex h-[300px] items-center justify-center  p-3 text-primary">
        <AiOutlineLoading3Quarters className="animate-spin text-[40px] text-primary lg:text-[60px]" />
      </div>
    );
  }

  if (isMaterial) {
    return (
      <div className="flex max-h-[300px] flex-col space-y-3 overflow-y-auto pr-3 text-primary">
        {Object.keys(groupedMaterialOptions).map((materialSubType) => {
          return (
            <div key={materialSubType} className="flex flex-col space-y-1">
              <span className="text-[14px]">{materialSubType}</span>
              <div key={materialSubType} className="grid grid-cols-4 gap-4">
                {groupedMaterialOptions[materialSubType].map(
                  (option, index) => {
                    if (
                      !showAllMaterialTypeOptions[materialSubType] &&
                      index > 2
                    ) {
                      return;
                    }
                    return (
                      <OptionCard
                        key={index}
                        option={option}
                        selectLayout={selectLayout}
                        selectShape={selectShape}
                        selectMaterial={selectMaterial}
                      />
                    );
                  }
                )}

                {groupedMaterialOptions[materialSubType].length > 3 && (
                  <div>
                    <div className="flex aspect-video w-full items-center justify-center">
                      <span
                        className="cursor-pointer text-[14px] font-medium underline"
                        onClick={() =>
                          setShowAllMaterialTypeOptions((prev) => {
                            return {
                              ...prev,
                              [materialSubType]: !prev[materialSubType],
                            };
                          })
                        }
                      >
                        {showAllMaterialTypeOptions[materialSubType]
                          ? "View Less"
                          : "View More"}
                      </span>
                    </div>
                    <span />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`${
        isLayout
          ? "flex w-full flex-wrap justify-evenly gap-3"
          : "grid max-h-[300px] grid-cols-3 gap-4 overflow-y-auto pr-3"
      }`}
    >
      {options?.map((option, index) => {
        return (
          <OptionCard
            key={index}
            option={option}
            selectLayout={selectLayout}
            selectShape={selectShape}
            selectMaterial={selectMaterial}
          />
        );
      })}
    </div>
  );
}

const OptionCard = ({
  option,
  selectLayout,
  selectShape,
  selectMaterial,
}: {
  option: LayoutSize | LayoutBody | Shape | Material;
  selectLayout: (layout: LayoutBody | LayoutSize) => void;
  selectShape: (selectedShape: Shape, elementOrder: number) => void;
  selectMaterial: (
    selectedMaterial: Material,
    elementOrder: number,
    materialOrder: number
  ) => void;
}) => {
  const { currentStepData, nextStep, nextButtonDisabled } = useStepper();
  const {
    layout_body,
    layout_size,
    shapes: selectedShapes,
    materials: selectedMaterials,
    priceMode,
  } = useSelector((state: { design: DesignState }) => state.design);

  const isLayout = currentStepData?.category === DESIGN_CATEGORY.LAYOUT;
  const isLayoutBody = currentStepData?.type === LAYOUT_TYPES.LAYOUT_BODY;
  const isLayoutSize = currentStepData?.type === LAYOUT_TYPES.LAYOUT_SIZE;
  const isShape = currentStepData?.category === DESIGN_CATEGORY.SHAPE;

  const isSelected = isLayoutBody
    ? (option as LayoutBody).id === layout_body?.id
    : isLayoutSize
    ? (option as LayoutSize).id === layout_size?.id
    : isShape
    ? selectedShapes[currentStepData?.elementOrder]?.code === option?.code
    : selectedMaterials[
        `${currentStepData?.elementOrder}_${currentStepData?.materialOrder}`
      ]?.code === option?.code;

  const handleOptionClick = (
    option: LayoutSize | LayoutBody | Shape | Material
  ) => {
    isLayout
      ? selectLayout(option as LayoutBody | LayoutSize)
      : isShape
      ? selectShape(option as Shape, currentStepData?.elementOrder as number)
      : selectMaterial(
          option as Material,
          currentStepData?.elementOrder as number,
          currentStepData?.materialOrder as number
        );
  };

  const handleOptionDoubleClick = (
    option: LayoutSize | LayoutBody | Shape | Material
  ) => {
    handleOptionClick(option);

    if (!nextButtonDisabled) {
      nextStep();
    }
  };

  return (
    <div className={`relative flex max-w-[180px] w-full flex-col space-y-1`}>
      <CardWrapper
        isSelected={!!isSelected}
        onClick={() => handleOptionClick(option)}
        onDoubleClick={() => handleOptionDoubleClick(option)}
        className={`aspect-video w-full ${
          !isLayout ? "border-0" : "border-1 p-3"
        } ${isSelected && "scale-[80%] border-2"}`}
      >
        <img
          key={option.name}
          src={option.image}
          alt={option.name}
          loading="lazy"
          className={`h-full w-full ${!isLayout && "object-cover"}`}
        />
      </CardWrapper>
      {isLayout ? (
        <div className="flex flex-col space-y-1 text-center text-[14px] text-primary">
          <span>{option?.name}</span>
          {isLayoutSize && (option as LayoutSize).dimensions && (
            <span>{"(" + (option as LayoutSize).dimensions + ")"}</span>
          )}
        </div>
      ) : (
        <span className="text-[14px] text-primary">
          {formatToCurrency(
            option.price,
            CURRENCY,
            +option?.range_price_percentage,
            priceMode
          )}
        </span>
      )}
    </div>
  );
};

export default OptionsContainer;
